import React, { useState, useEffect } from 'react';
import { useUser, UserProfile } from '@clerk/clerk-react';
import { Bell, CreditCard, Shield, Key, Download, ExternalLink, FileText, Loader } from 'lucide-react';
import { paymentService } from '../../../services/api/paymentService';

const Settings = () => {
  const { user, isLoaded } = useUser();
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(null);
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    marketingUpdates: false
  });
  const [savingPreferences, setSavingPreferences] = useState(false);

  useEffect(() => {
    if (isLoaded && user) {
      // Load preferences from Clerk metadata
      if (user.unsafeMetadata?.preferences) {
        setPreferences(user.unsafeMetadata.preferences);
      }
      
      if (user.id) {
        console.log('Loading payment history for user:', user.id);
        loadPaymentHistory();
      }
    }
  }, [isLoaded, user]);

  const loadPaymentHistory = async () => {
    if (!user?.id) return;
    
    setLoadingPayments(true);
    try {
      const response = await paymentService.getPaymentHistory(user.id);
      console.log('Payment history loaded:', response);
      // Handle both array and object response formats
      const historyData = Array.isArray(response) ? response : (response.payments || []);
      setPayments(historyData);
    } catch (error) {
      console.error('Failed to load payment history:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handlePreferenceChange = async (key) => {
    if (!user) return;
    
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key]
    };
    
    setPreferences(newPreferences);
    setSavingPreferences(true);
    
    try {
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          preferences: newPreferences
        }
      });
    } catch (error) {
      console.error('Failed to save preferences:', error);
      // Revert on error
      setPreferences(preferences);
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleViewReceipt = async (paymentId) => {
    setReceiptLoading(paymentId);
    try {
      const { receiptUrl } = await paymentService.getReceiptUrl(paymentId);
      if (receiptUrl) {
        window.open(receiptUrl, '_blank');
      } else {
        alert('Receipt not available yet. Please try again later.');
      }
    } catch (error) {
      console.error('Failed to get receipt URL:', error);
      alert('Could not retrieve receipt. Please contact support.');
    } finally {
      setReceiptLoading(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500">
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      
      {/* Clerk User Profile - Handles most account settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <UserProfile 
          appearance={{
            elements: {
              rootBox: "w-full",
              card: "shadow-none border-none w-full",
              navbar: "hidden",
              pageScrollBox: "p-6",
              headerTitle: "hidden",
              headerSubtitle: "hidden"
            }
          }} 
        />
      </div>

      {/* Payment History Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" id="billing">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-gray-500" />
          Billing & Payment History
        </h2>
        
        {loadingPayments ? (
          <div className="flex justify-center py-8">
            <Loader className="h-6 w-6 text-gray-400 animate-spin" />
          </div>
        ) : payments.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(payment.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-gray-900">
                      <div className="font-medium">Trademark Filing</div>
                      <div className="text-gray-500 text-xs">{payment.applicationId ? `App ID: ${payment.applicationId.slice(0, 8).toUpperCase()}` : 'New Application'}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${(payment.amount / 100).toFixed(2)}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        payment.status === 'succeeded' ? 'bg-green-100 text-green-800' : 
                        payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {payment.status === 'succeeded' ? 'Paid' : payment.status}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={() => handleViewReceipt(payment.id)}
                        disabled={receiptLoading === payment.id}
                        className="text-[#00695c] hover:text-[#004d40] inline-flex items-center gap-1 transition-colors disabled:opacity-50"
                      >
                        {receiptLoading === payment.id ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <FileText className="h-4 w-4" />
                        )}
                        View Receipt
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100 border-dashed">
            <CreditCard className="mx-auto h-8 w-8 text-gray-300 mb-2" />
            <p className="text-gray-500">No payment history found</p>
          </div>
        )}
      </div>

      {/* Additional Application Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="h-5 w-5 text-gray-500" />
          Notification Preferences
          {savingPreferences && <Loader className="h-4 w-4 animate-spin text-gray-400" />}
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-50">
            <div>
              <p className="font-medium text-gray-900">Email Notifications</p>
              <p className="text-sm text-gray-500">Receive updates about your trademark status</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={preferences.emailNotifications}
                onChange={() => handlePreferenceChange('emailNotifications')}
                disabled={savingPreferences}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00695c]"></div>
            </label>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-gray-900">Marketing Updates</p>
              <p className="text-sm text-gray-500">Receive news about new features and promotions</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={preferences.marketingUpdates}
                onChange={() => handlePreferenceChange('marketingUpdates')}
                disabled={savingPreferences}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00695c]"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
